import { NavLink, Outlet } from "react-router-dom";
import { 
  LayoutDashboard, 
  FilePlus, 
  History, 
  Settings, 
  Anchor,
  Menu,
  X,
  Users,
  Factory,
  MapPin,
  Fish,
  FileText,
  BarChart3,
  Download,
  Upload,
  Truck,
  FolderOpen,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { downloadBackup, importBackup } from "@/services/localDatabase";

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [archiviOpen, setArchiviOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handleBackup = async () => {
    try {
      const result = await downloadBackup();
      if (result.success) {
        if (result.path) {
          toast.success(`Backup salvato in Documenti/${result.filename}`);
        } else if (result.shared) {
          toast.success("Backup condiviso con successo");
        } else {
          toast.success(`Backup salvato: ${result.filename}`);
        }
      }
    } catch (error) {
      console.error("Backup error:", error);
      toast.error("Errore nel backup");
    }
  };

  const handleRestore = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error("Seleziona un file .json di backup");
      return;
    }

    if (!window.confirm("ATTENZIONE: Il ripristino sostituirà TUTTI i dati esistenti. Continuare?")) {
      e.target.value = '';
      return;
    }

    try {
      // Read file content
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      // Validate backup structure
      if (!backupData.version || !backupData.data) {
        throw new Error("File di backup non valido");
      }
      
      const result = await importBackup(backupData);
      if (result.success) {
        toast.success(`Ripristino completato! Importati: ${JSON.stringify(result.imported)}`);
        // Reload the page to refresh all data
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error) {
      console.error("Restore error:", error);
      toast.error(error.message || "Errore nel ripristino");
    }
    
    e.target.value = '';
  };

  const navItems = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/nuovo", icon: FilePlus, label: "Nuovo DDR" },
    { to: "/storico", icon: History, label: "Storico" },
    { to: "/report", icon: BarChart3, label: "Report" },
  ];

  const archiviItems = [
    { to: "/produttori", icon: Factory, label: "Produttori" },
    { to: "/destinatari", icon: Users, label: "Destinatari DDR" },
    { to: "/destinatari-ddt", icon: Truck, label: "Destinatari DDT" },
    { to: "/ubicazioni", icon: MapPin, label: "Ubicazioni" },
    { to: "/specie", icon: Fish, label: "Specie" },
    { to: "/causali", icon: FileText, label: "Causali" },
  ];

  return (
    <div className="app-layout">
      {/* Mobile menu button */}
      <Button
        data-testid="mobile-menu-btn"
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 md:hidden bg-slate-900 text-white hover:bg-slate-800"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </Button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`sidebar ${sidebarOpen ? 'open' : ''}`}
        data-testid="sidebar"
      >
        <div className="p-6">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <Anchor className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-barlow text-lg font-bold tracking-wide">RM DDR</h1>
              <p className="text-xs text-slate-400">Gestione Documenti</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'active' : ''}`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon size={20} />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}

            {/* Archivi Dropdown */}
            <div>
              <button
                onClick={() => setArchiviOpen(!archiviOpen)}
                className="nav-link w-full justify-between"
                data-testid="nav-archivi"
              >
                <div className="flex items-center gap-3">
                  <FolderOpen size={20} />
                  <span className="font-medium">Archivi</span>
                </div>
                {archiviOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              
              {archiviOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l border-slate-600 pl-3">
                  {archiviItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      className={({ isActive }) =>
                        `nav-link text-sm py-2 ${isActive ? 'active' : ''}`
                      }
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon size={16} />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {/* Impostazioni */}
            <NavLink
              to="/impostazioni"
              data-testid="nav-impostazioni"
              className={({ isActive }) =>
                `nav-link ${isActive ? 'active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Settings size={20} />
              <span className="font-medium">Impostazioni</span>
            </NavLink>
          </nav>
        </div>

        {/* Footer with Backup/Restore */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
          <div className="flex gap-2 mb-3">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1 text-xs bg-slate-700 border-slate-600 hover:bg-slate-600 text-white"
              onClick={handleBackup}
              data-testid="backup-btn"
            >
              <Download className="w-3 h-3 mr-1" />
              Backup
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1 text-xs bg-slate-700 border-slate-600 hover:bg-slate-600 text-white"
              onClick={handleRestore}
              data-testid="restore-btn"
            >
              <Upload className="w-3 h-3 mr-1" />
              Ripristina
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="restore-file-input"
          />
          <p className="text-xs text-slate-500 text-center">
            DDR - Molluschi Bivalvi
          </p>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
